import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * useAFKDetection — tracks user inactivity and surfaces an "are you still
 * there?" prompt so we can release the socket / stop polling when a player
 * walks away from the tab.
 *
 * Default behaviour:
 *   - 5 minutes of no input  -> isAFK = true (callers show a modal)
 *   - 60 seconds in the modal with no response -> calls onTimeout()
 *
 * Activity = mousemove, mousedown, keydown, touchstart, scroll, wheel.
 * Activity events while isAFK is true are ignored — the player must
 * explicitly confirm via the modal so accidental wakes don't dismiss it.
 *
 * Options:
 *   idleMs        — ms before we consider the user AFK   (default 5 * 60_000)
 *   warningMs     — ms the modal stays open before timeout (default 60_000)
 *   onTimeout     — called when warningMs elapses with no confirm
 *   enabled       — bool, defaults true. Set false to pause detection.
 */
export default function useAFKDetection({
  idleMs = 5 * 60 * 1000,
  warningMs = 60 * 1000,
  onTimeout,
  enabled = true,
} = {}) {
  const [isAFK, setIsAFK] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(Math.floor(warningMs / 1000));
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const tickTimerRef = useRef(null);
  const onTimeoutRef = useRef(onTimeout);
  // v641.5 bugfix: track isAFK in a ref so the activity handler can read the
  // latest value without making the main effect depend on isAFK (which would
  // tear down listeners and re-arm timers every time isAFK toggles).
  const isAFKRef = useRef(false);

  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);
  useEffect(() => { isAFKRef.current = isAFK; }, [isAFK]);

  const clearTimers = useCallback(() => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
    if (warningTimerRef.current) { clearTimeout(warningTimerRef.current); warningTimerRef.current = null; }
    if (tickTimerRef.current) { clearInterval(tickTimerRef.current); tickTimerRef.current = null; }
  }, []);

  const armIdle = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => {
      setIsAFK(true);
      setSecondsLeft(Math.floor(warningMs / 1000));
      // Start countdown ticks for UI
      tickTimerRef.current = setInterval(() => {
        setSecondsLeft(s => (s > 0 ? s - 1 : 0));
      }, 1000);
      // Hard timeout — fire onTimeout if the player doesn't respond
      warningTimerRef.current = setTimeout(() => {
        try { onTimeoutRef.current && onTimeoutRef.current(); } catch (e) { /* swallow */ }
      }, warningMs);
    }, idleMs);
  }, [idleMs, warningMs]);

  /**
   * dismiss — caller invokes when the player confirms they're still here.
   * Resets idle clock and hides the modal.
   */
  const dismiss = useCallback(() => {
    clearTimers();
    setIsAFK(false);
    setSecondsLeft(Math.floor(warningMs / 1000));
    if (enabled) armIdle();
  }, [clearTimers, armIdle, enabled, warningMs]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      setIsAFK(false);
      return undefined;
    }

    armIdle();

    const handleActivity = () => {
      // Ignore activity while AFK modal is up — player must click "Yes"
      if (isAFKRef.current) return;
      armIdle();
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'];
    events.forEach(ev => window.addEventListener(ev, handleActivity, { passive: true }));

    return () => {
      events.forEach(ev => window.removeEventListener(ev, handleActivity));
      clearTimers();
    };
  }, [enabled, armIdle, clearTimers]);

  return { isAFK, secondsLeft, dismiss };
}
